import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  Sparkles, Wand2, Database, Eye, Loader, X, ArrowLeft, Calendar, Hash, MapPin, Image as ImageIcon,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './ThemedText';
import { Button } from './Button';
import { LocationEditorMap } from './LocationEditorMap';
import { demoDataService } from '../api';
import { ModalOverlay as Modal, ModalContent, ModalHeader } from './ModalStyles';

// ─── Styled Components (mirrors ExcelImportPage's review styling) ─────

const HelpNote = styled.p`
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  color: ${props => props.theme.colors.muted};
  font-size: 13px;
  line-height: 1.5;
`;

const Field = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const FieldHint = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${props => props.theme.colors.muted};
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 64px;
  resize: vertical;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const PreviewBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const PreviewCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const AiBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
  background: ${props => props.theme.colors.tint};
`;

const FallbackNote = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
`;

const TableWrapper = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: auto;
  max-height: 360px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.muted};
  font-weight: 600;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  vertical-align: top;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

// ─── Helpers ─────────────────────────────────────────────────────────

const MAX_PREVIEW_COLUMNS = 6;

/** A YYYY-MM-DD string for <input type="date">, `days` ago from now (0 = today). */
const dateInputDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

/** Convert a YYYY-MM-DD date-input value to an ISO string (or null when blank). */
const dateInputToIso = (value) => (value ? new Date(value).toISOString() : null);

/** Collect up to MAX_PREVIEW_COLUMNS representative field keys, in first-seen order. */
const deriveColumns = (rows) => {
  const keys = [];
  rows.forEach((r) => {
    Object.keys(r.fields || {}).forEach((k) => {
      if (!keys.includes(k)) keys.push(k);
    });
  });
  return keys.slice(0, MAX_PREVIEW_COLUMNS);
};

/** Render any field value as a short, human-readable cell string. */
const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  // Geo point → "lat, lng" instead of raw JSON.
  if (typeof value === 'object' && value.latitude != null && value.longitude != null)
    return `${Number(value.latitude).toFixed(4)}, ${Number(value.longitude).toFixed(4)}`;
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

// ─── Component ───────────────────────────────────────────────────────

/**
 * Modal that lets a demo-org builder AI-generate realistic dummy data for a form, work-order list, or
 * customer list. Flow: config (count / date range / tone) → preview → commit.
 * Entity-agnostic: pass `entityType` ('forms' | 'workorders' | 'customers') + `targetId`. The legacy
 * `formSchemaId`/`formName` props still work (default entityType 'forms').
 */
export const GenerateDemoDataDialog = ({
  formSchemaId, formName,
  entityType = 'forms', targetId, entityName, entityNoun = 'submissions',
  open, onClose, onCommitted,
}) => {
  const theme = useTheme();
  const resolvedTarget = targetId ?? formSchemaId;
  const resolvedName = entityName || formName;
  const showSubmitter = entityType === 'forms';
  const showTone = entityType !== 'locations';            // locations are all-Bogus + pin; the AI tone is moot
  const supportsGeoPin = entityType === 'locations' || entityType === 'forms';
  const geoAlwaysOn = entityType === 'locations';         // locations are inherently geo → pin always shown

  // 'config' = gather options, 'preview' = review generated rows before committing.
  const [step, setStep] = useState('config');

  // Config state
  const [recordCount, setRecordCount] = useState(25);
  const [dateFrom, setDateFrom] = useState(dateInputDaysAgo(90));
  const [dateTo, setDateTo] = useState(dateInputDaysAgo(0));
  const [tone, setTone] = useState('');

  // Geo pin (Locations always; Forms via the opt-in toggle below). Default centre = Toronto, drag to taste.
  const [useGeoPin, setUseGeoPin] = useState(geoAlwaysOn);
  const [centerLat, setCenterLat] = useState(43.6532);
  const [centerLng, setCenterLng] = useState(-79.3832);
  const [radiusKm, setRadiusKm] = useState(10);

  // Preview / commit state
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [committing, setCommitting] = useState(false);

  // Image/Drawing field keys the builder opted into AI generation (applied at commit). Round 7.
  const [aiImageFields, setAiImageFields] = useState([]);

  // Reset to a clean config step each time the dialog is opened.
  useEffect(() => {
    if (!open) return;
    setStep('config');
    setRecordCount(25);
    setDateFrom(dateInputDaysAgo(90));
    setDateTo(dateInputDaysAgo(0));
    setTone('');
    setUseGeoPin(geoAlwaysOn);
    setCenterLat(43.6532);
    setCenterLng(-79.3832);
    setRadiusKm(10);
    setPreview(null);
    setGenerating(false);
    setCommitting(false);
    setAiImageFields([]);
  }, [open, geoAlwaysOn]);

  const columns = useMemo(() => deriveColumns(preview?.rows || []), [preview]);

  // Distinct Image/Drawing field keys present in the preview — each can be opted into AI generation.
  const imageFields = useMemo(() => {
    const out = [];
    (preview?.rows || []).forEach((r) => (r.media || []).forEach((m) => {
      const t = (m.attachmentType || '').toLowerCase();
      if ((t === 'image' || t === 'drawing') && m.fieldKey && !out.includes(m.fieldKey)) out.push(m.fieldKey);
    }));
    return out;
  }, [preview]);

  const handleGenerate = async () => {
    const count = Math.max(1, Math.min(500, parseInt(recordCount, 10) || 25));
    const pinned = geoAlwaysOn || useGeoPin;
    setGenerating(true);
    setAiImageFields([]); // a fresh preview clears any prior image-field selections
    try {
      const result = await demoDataService.previewDemoData(entityType, parseInt(resolvedTarget, 10), {
        recordCount: count,
        dateFrom: dateInputToIso(dateFrom),
        dateTo: dateInputToIso(dateTo),
        tone: tone.trim() || null,
        seed: null,
        fieldOverrides: null,
        // Only send the pin when it's in play — otherwise forms keep resolving geo from the AI/tone.
        centerLat: pinned ? centerLat : null,
        centerLng: pinned ? centerLng : null,
        radiusKm: pinned ? radiusKm : null,
      });
      setPreview(result);
      setStep('preview');
    } catch (err) {
      console.error('Failed to generate demo data preview:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Something went wrong');
    } finally {
      setGenerating(false);
    }
  };

  const handleCommit = async () => {
    const rows = preview?.rows || [];
    if (rows.length === 0) return;
    setCommitting(true);
    try {
      const result = await demoDataService.commitDemoData(entityType, parseInt(resolvedTarget, 10), rows, aiImageFields);
      const skippedNote = result?.skipped > 0 ? ` (${result.skipped} skipped)` : '';
      toast.success(`Created ${result?.created ?? 0} demo ${entityNoun}${skippedNote}`);
      onCommitted?.();
      onClose?.();
    } catch (err) {
      console.error('Failed to commit demo data:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Something went wrong');
    } finally {
      setCommitting(false);
    }
  };

  const rowCount = preview?.rows?.length || 0;

  return (
    <Modal theme={theme} isOpen={open}>
      <ModalContent theme={theme} style={{ maxWidth: 720 }}>
        <ModalHeader theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${theme.colors.tint}, ${theme.colors.primary})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={18} color="#fff" />
            </div>
            <div>
              <ThemedText type="subtitle" style={{ margin: 0 }}>Generate demo data</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted, margin: 0 }}>
                {resolvedName || 'This demo'}
              </ThemedText>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose} style={{ padding: '4px 8px', minWidth: 'auto' }}>
            <X size={14} />
          </Button>
        </ModalHeader>

        {step === 'config' && (
          <div>
            <HelpNote theme={theme}>
              Fill in a few options and we'll generate realistic sample {entityNoun}. Nothing
              is saved until you review the preview and confirm.
            </HelpNote>

            <Field theme={theme}>
              <FieldLabel theme={theme}>
                <Hash size={14} /> How many records?
              </FieldLabel>
              <TextInput
                theme={theme}
                type="number"
                min={1}
                max={500}
                value={recordCount}
                onChange={(e) => setRecordCount(e.target.value)}
              />
            </Field>

            <Field theme={theme}>
              <FieldLabel theme={theme}>
                <Calendar size={14} /> Date range <FieldHint theme={theme}>— when these submissions were "collected"</FieldHint>
              </FieldLabel>
              <DateRow theme={theme}>
                <TextInput
                  theme={theme}
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextInput
                  theme={theme}
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </DateRow>
            </Field>

            {showTone && (
              <Field theme={theme}>
                <FieldLabel theme={theme}>
                  <Wand2 size={14} /> Context / tone <FieldHint theme={theme}>— optional</FieldHint>
                </FieldLabel>
                <TextArea
                  theme={theme}
                  value={tone}
                  placeholder="e.g. municipal water inspections in Ontario"
                  onChange={(e) => setTone(e.target.value)}
                />
              </Field>
            )}

            {supportsGeoPin && (
              <Field theme={theme}>
                <FieldLabel theme={theme}>
                  <MapPin size={14} /> {geoAlwaysOn ? 'Where to place these locations' : 'Pin a location'}
                  {!geoAlwaysOn && (
                    <FieldHint theme={theme}>— optional, overrides the AI's guess</FieldHint>
                  )}
                </FieldLabel>

                {!geoAlwaysOn && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useGeoPin}
                      onChange={(e) => setUseGeoPin(e.target.checked)}
                    />
                    Pin a location on the map to place this data
                  </label>
                )}

                {(geoAlwaysOn || useGeoPin) && (
                  <>
                    <LocationEditorMap
                      latitude={centerLat}
                      longitude={centerLng}
                      onChange={(la, ln) => { setCenterLat(la); setCenterLng(ln); }}
                      theme={theme}
                      height="220px"
                    />
                    <FieldLabel theme={theme} style={{ marginTop: 14 }}>
                      Scatter radius <FieldHint theme={theme}>— {radiusKm} km around the pin</FieldHint>
                    </FieldLabel>
                    <input
                      type="range"
                      min={0.5}
                      max={100}
                      step={0.5}
                      value={radiusKm}
                      onChange={(e) => setRadiusKm(parseFloat(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </>
                )}
              </Field>
            )}

            <Footer theme={theme}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleGenerate} disabled={generating}>
                {generating ? <><Loader size={14} /> Generating…</> : <><Sparkles size={14} /> Generate preview</>}
              </Button>
            </Footer>
          </div>
        )}

        {step === 'preview' && (
          <div>
            <HelpNote theme={theme}>
              Review the generated submissions below. Nothing is saved until you confirm.
            </HelpNote>

            <PreviewBar theme={theme}>
              <Eye size={16} color={theme.colors.muted} />
              <PreviewCount theme={theme}>
                {rowCount} record{rowCount === 1 ? '' : 's'}
              </PreviewCount>
              {preview?.aiEnriched
                ? <AiBadge theme={theme}><Sparkles size={11} /> AI</AiBadge>
                : <FallbackNote theme={theme}>generated from a fallback template</FallbackNote>}
            </PreviewBar>

            {rowCount > 0 ? (
              <TableWrapper theme={theme}>
                <Table>
                  <thead>
                    <tr>
                      {columns.map((key) => <Th key={key} theme={theme}>{key}</Th>)}
                      <Th theme={theme}>Date</Th>
                      {showSubmitter && <Th theme={theme}>Submitter</Th>}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i}>
                        {columns.map((key) => (
                          <Td key={key} theme={theme} title={formatCell(r.fields?.[key])}>
                            {formatCell(r.fields?.[key])}
                          </Td>
                        ))}
                        <Td theme={theme}>{formatDate(r.createdAt)}</Td>
                        {showSubmitter && (
                          <Td theme={theme} title={r.submitterName || r.submitterEmail || ''}>
                            {r.submitterName || r.submitterEmail || '—'}
                          </Td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <ThemedText style={{ color: theme.colors.muted }}>No records were generated.</ThemedText>
            )}

            {imageFields.length > 0 && (
              <Field theme={theme} style={{ marginTop: theme.spacing.lg }}>
                <FieldLabel theme={theme}>
                  <ImageIcon size={14} /> AI images
                  <FieldHint theme={theme}>— generated when you commit; otherwise placeholder graphics</FieldHint>
                </FieldLabel>
                {imageFields.map((key) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={aiImageFields.includes(key)}
                      onChange={(e) => setAiImageFields((prev) =>
                        e.target.checked ? [...prev, key] : prev.filter((k) => k !== key))}
                    />
                    Generate “{key}” with AI
                  </label>
                ))}
              </Field>
            )}

            <Footer theme={theme}>
              <Button variant="secondary" onClick={() => setStep('config')} disabled={committing}>
                <ArrowLeft size={14} /> Back
              </Button>
              <Button variant="primary" onClick={handleCommit} disabled={committing || rowCount === 0}>
                {committing
                  ? <><Loader size={14} /> Saving…</>
                  : <><Database size={14} /> Generate {rowCount} record{rowCount === 1 ? '' : 's'}</>}
              </Button>
            </Footer>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default GenerateDemoDataDialog;
